import React, { useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import { useGetSingleEntitySchema } from '@blaze-cms/react-page-builder';

const FileButton = ({
  modifier,
  fileProperty,
  numberOfFiles,
  displayName,
  children,
  parent: { itemEntity, itemId }
}) => {
  const limit = numberOfFiles;

  const { data: { getEntitySchema = {} } = {}, loading } = useGetSingleEntitySchema(
    itemEntity,
    !children
  );

  const { actions: { getPublished = 'getPublishedProduct' } = {} } = getEntitySchema;

  const query = useMemo(
    () => gql`
    query getFiles($id: String!) {
      entityData: ${getPublished} (id: $id) {
        id
        files: ${fileProperty} {
            name
            url
        }
      }
    }
  `,
    [getPublished, fileProperty]
  );

  const { data: { entityData = [] } = {}, loading: dataLoading } = useQuery(query, {
    variables: {
      id: itemId
    },
    skip: loading || !fileProperty
  });

  if (!fileProperty || !children || dataLoading || loading) return null;

  let files = Array.isArray(entityData.files) ? entityData.files : [entityData.files];
  files = limit ? files.slice(0, limit) : files;

  if (!files.length) return null;

  const getFileProps = file => {
    const { url, name } = file;
    const fileProps = {
      url
    };

    if (displayName) fileProps.text = name;

    return fileProps;
  };

  const WrapperTag = modifier ? 'div' : Fragment;

  return (
    <WrapperTag className={modifier}>
      {files.map(file => updateChildSettings(children, getFileProps(file)))}
    </WrapperTag>
  );
};

const updateChildSettings = (children, otherProps = {}) =>
  React.Children.map(children, child => {
    const childrenElements = child.props.children;
    if (Array.isArray(childrenElements) && childrenElements.length) {
      const [, nestedChildren] = child.props.children;
      return (
        Array.isArray(nestedChildren) &&
        nestedChildren.map(nestedChild => updateChildSettings(nestedChild, otherProps))
      );
    }

    const newProps = {
      component: {
        ...child.props.component,
        settings: {
          ...child.props.component.settings,
          ...otherProps
        }
      }
    };
    return React.cloneElement(child, { ...newProps });
  });

FileButton.propTypes = {
  modifier: PropTypes.string,
  fileProperty: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(PropTypes.element),
  parent: PropTypes.objectOf(PropTypes.any).isRequired,
  numberOfFiles: PropTypes.number.isRequired,
  displayName: PropTypes.bool
};

FileButton.defaultProps = {
  modifier: '',
  children: null,
  displayName: false
};

export default FileButton;
